// checkpoint-alert — banner rendered when a run is paused waiting for human
// approval. Visual urgency requires 1s tick; cost is 1 setInterval per active
// alert (max 1 per page). Cleared on unmount.

import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { ShieldAlert } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/utils";
import { SuggestBanner } from "@/components/ui/suggest-banner/suggest-banner";
import type { Checkpoint } from "@/types/workflow.types";

// ── Helpers ───────────────────────────────────────────────────────────────────

function remainingMs(expiresAt?: string | null): number | null {
  if (!expiresAt) return null;
  return new Date(expiresAt).getTime() - Date.now();
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return "Expired";
  const totalSec = Math.floor(ms / 1000);
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

// ── Component ─────────────────────────────────────────────────────────────────

interface CheckpointAlertProps {
  checkpoint: Checkpoint;
  canCancel?: boolean;
  onCancel?: () => void;
  isCancelling?: boolean;
}

export function CheckpointAlert({
  checkpoint,
  canCancel = false,
  onCancel,
  isCancelling = false,
}: CheckpointAlertProps) {
  const t = useTranslations("workflows");

  const [remaining, setRemaining] = useState<number | null>(
    () => remainingMs(checkpoint.expiresAt)
  );

  // 1s tick for countdown urgency display.
  useEffect(() => {
    if (!checkpoint.expiresAt) return;
    const id = setInterval(() => {
      setRemaining(remainingMs(checkpoint.expiresAt));
    }, 1000);
    return () => clearInterval(id);
  }, [checkpoint.expiresAt]);

  const isUrgent = remaining != null && remaining > 0 && remaining < 10 * 60 * 1000;
  const isExpired = remaining != null && remaining <= 0;
  const tone = isExpired ? "danger" : isUrgent ? "danger" : "warning";

  const countdownText = remaining != null
    ? isExpired
      ? t("run.checkpoint.expired")
      : `${formatRemaining(remaining)} ${t("run.checkpoint.remaining")}`
    : t("run.checkpoint.no_expiry");

  return (
    <SuggestBanner
      tone={tone}
      icon={<ShieldAlert size={14} aria-hidden />}
      title={checkpoint.title || t("run.checkpoint.default_title")}
      description={countdownText}
      actions={
        <div className="flex items-center gap-2">
          <Link
            to="/checkpoints/$id"
            params={{ id: checkpoint.id }}
            className={cn(
              "text-[11px] font-semibold px-2.5 py-1 rounded-[var(--radius-full)]",
              "bg-[var(--warning)] text-white hover:opacity-90 transition-opacity duration-[120ms]",
              isExpired && "bg-[var(--destructive)]"
            )}
          >
            {t("run.checkpoint.review_cta")}
          </Link>
          {canCancel && !isExpired && (
            <button
              type="button"
              disabled={isCancelling}
              onClick={onCancel}
              className={cn(
                "text-[11px] font-medium text-[var(--muted-foreground)]",
                "hover:text-[var(--foreground)] transition-colors duration-[120ms]",
                "disabled:opacity-50"
              )}
            >
              {isCancelling
                ? t("run.checkpoint.cancelling")
                : t("run.checkpoint.cancel_cta")}
            </button>
          )}
        </div>
      }
    />
  );
}
